import { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Divider, Button, Form, Typography } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as CancelSvg } from '../../../../assets/icons/cancel.svg';
import { ReactComponent as CancelBlackSvg } from '../../../../assets/icons/cancel-black.svg';
import { ReactComponent as FormSvg } from '../../../../assets/icons/form-white.svg';
import { ReactComponent as FormBlackSvg } from '../../../../assets/icons/form-black.svg';
import { useTheme } from '../../../../context/ThemeContext';
import { ErrorMessageContext } from '../../../../context/ErrorMessageContext';
import { API } from '../../../../services';
import { API_RESPONSE_STATUS } from '../../../../utilities/constants';
import {
  sanitizeInput,
  getErrorMessage,
} from '../../../../utilities/commonFunctions';

const { Text } = Typography;

const SOModalLayout = ({
  isModalOpen,
  setIsModalOpen,
  form,
  type,
  loading,
  setLoading,
  updateSOSList,
  clickedSosData,
  title,
  footerButtonText,
  children,
}) => {
  const { theme } = useTheme();
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  const onFinish = async values => {
    let payload;
    if (type === 'action') {
      const { reporter, callType, sosType, description, assign_to } = values;
      payload = {
        messageid: clickedSosData?.messageid,
        reporter: sanitizeInput(reporter),
        call_type: callType,
        reason: sanitizeInput(sosType),
        description: sanitizeInput(description),
        assignee: sanitizeInput(assign_to),
      };
    } else if (type === 'resolution') {
      const { resolved_by, comments } = values;
      payload = {
        messageid: clickedSosData?.messageid,
        resolved_by: sanitizeInput(resolved_by),
        comments: sanitizeInput(comments),
        status: 'Closed',
      };
    }
    try {
      setLoading(true);
      const { data, status } = await API.updateSOActionData(payload);
      if (status === API_RESPONSE_STATUS.SUCCESS) {
        form.resetFields();
        setIsModalOpen(false);
        if (type === 'action') {
          addErrorMessage('Action taken successfully', 'success');
          updateSOSList(data, 'so-action-modal');
        } else if (type === 'resolution') {
          addErrorMessage('Ticket resolved successfully', 'success');
          updateSOSList(data, 'so-resolution-modal');
        }
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to submit! Please try again after sometime.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      open={isModalOpen}
      destroyOnHidden
      title={
        <div>
          <div
            className="broadcast-modal-title"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon component={theme === 'dark' ? FormSvg : FormBlackSvg} />
              <Text>{title}</Text>
            </div>
            <Icon
              component={theme === 'dark' ? CancelSvg : CancelBlackSvg}
              onClick={handleCancel}
            />
          </div>
          <Divider />
        </div>
      }
      footer={[
        <Button
          key="send"
          type="primary"
          htmlType="submit"
          loading={loading}
          onClick={() => form.submit()}
        >
          {footerButtonText}
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
      ]}
      className="broadcast-modal"
      closable={false}
      width="40%"
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        className="full-height broadcast-form"
        scrollToFirstError
        requiredMark={false}
      >
        {children}
      </Form>
    </Modal>
  );
};

SOModalLayout.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  updateSOSList: PropTypes.func.isRequired,
  clickedSosData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  title: PropTypes.string.isRequired,
  footerButtonText: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default SOModalLayout;
